import useReportSort from '../../components/reports/useReportSort';
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiSearch,
  FiSliders,
} from "react-icons/fi";
import ReportFilterModal from "../../components/reports/ReportFilterModal";
import SearchableSelect from "../../components/reports/SearchableSelect";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const dateOnly = (value) => new Date(value).toISOString().slice(0, 10);
const firstDay = () => {
  const date = new Date();
  date.setDate(1);
  return dateOnly(date);
};
const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const text = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const initialFilters = () => ({
  from: firstDay(),
  to: dateOnly(new Date()),
  storeId: "",
  source: "",
  branch: "",
  brand: "",
  category: "",
  agent: "",
  cashier: "",
  customer: "",
  voucher: "",
  search: "",
  minAmount: "",
  maxAmount: "",
  minRate: "",
  maxRate: "",
});
const unique = (rows, key) =>
  [...new Set(rows.map((row) => row[key]).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b)),
  );

function download(blob, name) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function SalesDetailedReportPage() {
  const { accessToken, user } = useAuth();
  const canGenerate = hasPermission(user, "reports.sales_detailed.generate");
  const canExport = hasPermission(user, "reports.sales_detailed.export");
  const [stores, setStores] = useState([]);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport, () => setPage(1));
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const filterOpen = false;
  const rows = report?.rows || [];

  const updateDraft = (key, value) =>
    setDraftFilters((current) => ({ ...current, [key]: value }));
  const loadStores = useCallback(async () => {
    try {
      setStores(
        (await apiRequest("/business-reports/sales-detailed/stores", {
          accessToken,
        })) || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Could not load report stores.");
    }
  }, [accessToken]);
  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const choices = useMemo(
    () => ({
      branches: unique(rows, "branch"),
      brands: unique(rows, "brand"),
      categories: unique(rows, "category"),
      agents: unique(rows, "agent"),
      cashiers: unique(rows, "cashier"),
      customers: unique(rows, "customer"),
    }),
    [rows],
  );
  const visibleRows = useMemo(() => {
    const query = text(appliedFilters.search);
    const minAmount = Number(appliedFilters.minAmount || 0);
    const maxAmount =
      appliedFilters.maxAmount === ""
        ? Infinity
        : Number(appliedFilters.maxAmount);
    const minRate = Number(appliedFilters.minRate || 0);
    const maxRate =
      appliedFilters.maxRate === "" ? Infinity : Number(appliedFilters.maxRate);
    return rows.filter((row) => {
      const searched =
        !query ||
        [
          row.documentNumber,
          row.customer,
          row.customerPhone,
          row.product,
          row.sku,
          row.additionalItemCode,
          row.variant,
          row.agent,
          row.cashier,
          row.store,
          row.brand,
          row.category,
          row.branch,
          row.lotCode,
        ].some((value) => text(value).includes(query));
      return (
        searched &&
        (!appliedFilters.source || row.source === appliedFilters.source) &&
        (!appliedFilters.branch || row.branch === appliedFilters.branch) &&
        (!appliedFilters.brand || row.brand === appliedFilters.brand) &&
        (!appliedFilters.category ||
          row.category === appliedFilters.category) &&
        (!appliedFilters.agent || row.agent === appliedFilters.agent) &&
        (!appliedFilters.cashier || row.cashier === appliedFilters.cashier) &&
        (!appliedFilters.customer ||
          row.customer === appliedFilters.customer) &&
        (!appliedFilters.voucher ||
          text(row.documentNumber).includes(text(appliedFilters.voucher))) &&
        Number(row.totalAmount || 0) >= minAmount &&
        Number(row.totalAmount || 0) <= maxAmount &&
        Number(row.unitPrice || 0) >= minRate &&
        Number(row.unitPrice || 0) <= maxRate
      );
    });
  }, [appliedFilters, rows]);
  const filteredSummary = useMemo(
    () =>
      visibleRows.reduce(
        (summary, row) => ({
          documents: summary.documents.add(
            `${row.source}:${row.documentNumber}`,
          ),
          quantity: summary.quantity + Number(row.quantity || 0),
          discount: summary.discount + Number(row.discount || 0),
          total: summary.total + Number(row.totalAmount || 0),
        }),
        { documents: new Set(), quantity: 0, discount: 0, total: 0 },
      ),
    [visibleRows],
  );
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const pageRows = visibleRows.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError("");
    try {
      const generated = await apiRequest(
        "/business-reports/sales-detailed/generate",
        {
          method: "POST",
          body: {
            from: draftFilters.from,
            to: draftFilters.to,
            ...(draftFilters.storeId ? { storeId: draftFilters.storeId } : {}),
          },
          accessToken,
        },
      );
      setReport(generated);
      setAppliedFilters({ ...draftFilters });
      setPage(1);
      setShowFilters(false);
    } catch (requestError) {
      setReport(null);
      setError(requestError.message || "Could not generate the sales report.");
    } finally {
      setLoading(false);
    }
  };
  const openFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(true);
  };
  const closeFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(false);
  };
  const exportExcel = async () => {
    if (!canExport || !report) return;
    setExporting(true);
    setError("");
    try {
      const params = new URLSearchParams({
        from: appliedFilters.from,
        to: appliedFilters.to,
        format: "XLSX",
      });
      if (appliedFilters.storeId) params.set("storeId", appliedFilters.storeId);
      const response = await apiRequest(
        `/business-reports/sales-detailed/export?${params.toString()}`,
        { accessToken, rawResponse: true },
      );
      const name =
        response.headers
          .get("content-disposition")
          ?.match(/filename="?([^";]+)"?/)?.[1] || "sales-detailed.xlsx";
      download(await response.blob(), name);
    } catch (requestError) {
      setError(requestError.message || "Could not export the sales report.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="business-report">
      <header className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-800">
              Business reports
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-950">
              Sales Detailed Report
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Full line-level sales register for POS bills and issued invoices.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary-300 hover:text-primary-800"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}{" "}
              Export Excel
            </button>
          </div>
        </div>
      </header>
      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:mx-6">
          {error}
        </div>
      )}
      <div className="flex min-w-0">
        <aside className="hidden">
          <div className="flex h-14 items-center border-b border-slate-100 px-3">
            <FiSliders className="shrink-0 text-lg text-primary-800" />
            <div
              className={`ml-3 flex min-w-[260px] items-center justify-between transition-opacity ${filterOpen ? "opacity-100" : "opacity-0"}`}
            >
              <span className="font-bold text-slate-900">Report filters</span>
              <button
                type="button"
                onClick={() => setDraftFilters(initialFilters())}
                className="text-xs font-bold text-primary-800 hover:text-primary-950"
              >
                Reset
              </button>
            </div>
          </div>
          <div
            className={`h-[calc(100%-3.5rem)] min-w-80 overflow-y-auto px-4 py-4 transition-opacity ${filterOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
          >
            <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
              Filter changes are pending until you click{" "}
              <strong>Generate</strong>.
            </p>
            <div className="space-y-3">
              <Label label="From date">
                <input
                  type="date"
                  value={draftFilters.from}
                  onChange={(event) => updateDraft("from", event.target.value)}
                  className="filter-input"
                />
              </Label>
              <Label label="To date">
                <input
                  type="date"
                  value={draftFilters.to}
                  onChange={(event) => updateDraft("to", event.target.value)}
                  className="filter-input"
                />
              </Label>
              <Label label="Godown / Store">
                <select
                  value={draftFilters.storeId}
                  onChange={(event) =>
                    updateDraft("storeId", event.target.value)
                  }
                  className="filter-input"
                >
                  <option value="">All stores</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name} ({store.code})
                    </option>
                  ))}
                </select>
              </Label>
              <Label label="Series">
                <select
                  value={draftFilters.source}
                  onChange={(event) =>
                    updateDraft("source", event.target.value)
                  }
                  className="filter-input"
                >
                  <option value="">All sales</option>
                  <option value="POS">POS</option>
                  <option value="SALES_INVOICE">Sales invoice</option>
                </select>
              </Label>
              <Label label="Branch">
                <Select
                  options={choices.branches}
                  value={draftFilters.branch}
                  onChange={(value) => updateDraft("branch", value)}
                />
              </Label>
              <Label label="Company / Brand">
                <Select
                  options={choices.brands}
                  value={draftFilters.brand}
                  onChange={(value) => updateDraft("brand", value)}
                />
              </Label>
              <Label label="Item group">
                <Select
                  options={choices.categories}
                  value={draftFilters.category}
                  onChange={(value) => updateDraft("category", value)}
                />
              </Label>
              <Label label="Agent">
                <Select
                  options={choices.agents}
                  value={draftFilters.agent}
                  onChange={(value) => updateDraft("agent", value)}
                />
              </Label>
              <Label label="Cashier">
                <Select
                  options={choices.cashiers}
                  value={draftFilters.cashier}
                  onChange={(value) => updateDraft("cashier", value)}
                />
              </Label>
              <Label label="Party / Customer">
                <Select
                  options={choices.customers}
                  value={draftFilters.customer}
                  onChange={(value) => updateDraft("customer", value)}
                />
              </Label>
              <Label label="Voucher / Bill no.">
                <input
                  value={draftFilters.voucher}
                  onChange={(event) =>
                    updateDraft("voucher", event.target.value)
                  }
                  placeholder="Bill number"
                  className="filter-input"
                />
              </Label>
              <Label label="Search">
                <div className="relative">
                  <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={draftFilters.search}
                    onChange={(event) =>
                      updateDraft("search", event.target.value)
                    }
                    placeholder="Product, barcode or lot"
                    className="filter-input pl-8"
                  />
                </div>
              </Label>
              <Range
                label="Sale amount"
                min={draftFilters.minAmount}
                max={draftFilters.maxAmount}
                onMin={(value) => updateDraft("minAmount", value)}
                onMax={(value) => updateDraft("maxAmount", value)}
              />
              <Range
                label="Rate"
                min={draftFilters.minRate}
                max={draftFilters.maxRate}
                onMin={(value) => updateDraft("minRate", value)}
                onMax={(value) => updateDraft("maxRate", value)}
              />
            </div>
          </div>
        </aside>
        <main className="min-w-0 flex-1">
          {!report ? (
            <section className="flex min-h-[55vh] items-center justify-center bg-white px-6 text-center mt-4">
              <div>
                <FiFileText className="mx-auto mb-3 text-4xl text-primary-300" />
                <h2 className="font-bold text-slate-900">
                  Generate the sales register
                </h2>
                <p className="mt-1 max-w-sm text-sm text-slate-500">
                  Choose the report period and any filters, then click Generate.
                  The report will not change while filters are being edited.
                </p>
              </div>
            </section>
          ) : (
            <div className="space-y-4">
              <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5 mt-4">
                <Metric
                  label="Documents"
                  value={filteredSummary.documents.size.toLocaleString("en-IN")}
                />
                <Metric
                  label="Sale lines"
                  value={visibleRows.length.toLocaleString("en-IN")}
                />
                <Metric
                  label="Quantity"
                  value={filteredSummary.quantity.toLocaleString("en-IN", {
                    maximumFractionDigits: 3,
                  })}
                />
                <Metric
                  label="Discount"
                  value={money(filteredSummary.discount)}
                />
                <Metric
                  label="Net amount"
                  value={money(filteredSummary.total)}
                />
              </section>
              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h2 className="font-bold text-slate-950">Sales register</h2>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {visibleRows.length.toLocaleString("en-IN")} matching
                      lines · Generated after the last click of Generate
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                    Rows per page
                    <select
                      value={pageSize}
                      onChange={(event) => {
                        setPageSize(Number(event.target.value));
                        setPage(1);
                      }}
                      className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm font-semibold text-slate-800 outline-none focus:border-primary-700"
                    >
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                    </select>
                  </label>
                </div>
                <div className="overflow-x-auto">
                  {sortControls}
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-left uppercase tracking-wide text-slate-600">
                      <tr>
                        {[
                          "Sno.",
                          "Company name",
                          "Additional item code",
                          "Item group name",
                          "Item name",
                          "Pack / grade",
                          "Bill date",
                          "Bill no.",
                          "Total qty",
                          "M.R.P.",
                          "Discount",
                          "Net amount",
                          "Agent name",
                          "Sale return qty",
                          "Lot code",
                          "Pur rate",
                          "Cashier",
                          "Counter",
                          "Godown / Store",
                          "Branch",
                          "Series",
                          "GST %",
                        ].map((header) => (
                          <th
                            key={header}
                            className="whitespace-nowrap border-b border-slate-200 px-3 py-3 font-bold"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((row, index) => (
                        <tr
                          key={`${row.source}-${row.documentNumber}-${row.sku}-${index}`}
                          className="border-b border-slate-100 text-slate-700 hover:bg-primary-50/30"
                        >
                          <td className="px-3 py-2.5 text-slate-500">
                            {(page - 1) * pageSize + index + 1}
                          </td>
                          <td className="px-3 py-2.5">{row.brand || "—"}</td>
                          <td className="px-3 py-2.5 font-mono">
                            {row.additionalItemCode || row.sku || "—"}
                          </td>
                          <td className="px-3 py-2.5">{row.category || "—"}</td>
                          <td className="px-3 py-2.5 font-semibold text-slate-900">
                            {row.product}
                          </td>
                          <td className="px-3 py-2.5">{row.variant || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            {dateOnly(row.date)}
                          </td>
                          <td className="px-3 py-2.5 font-semibold text-primary-800">
                            {row.documentNumber}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {row.quantity}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {row.mrp === null || row.mrp === undefined
                              ? "—"
                              : money(row.mrp)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-red-600">
                            {money(row.discount)}
                          </td>
                          <td className="px-3 py-2.5 text-right font-bold text-slate-900">
                            {money(row.totalAmount)}
                          </td>
                          <td className="px-3 py-2.5">{row.agent || "—"}</td>
                          <td className="px-3 py-2.5 text-right">
                            {row.saleReturnQuantity || "—"}
                          </td>
                          <td className="px-3 py-2.5 font-mono">
                            {row.lotCode || "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {row.purchaseRate === null ||
                            row.purchaseRate === undefined
                              ? "—"
                              : money(row.purchaseRate)}
                          </td>
                          <td className="px-3 py-2.5">{row.cashier || "—"}</td>
                          <td className="px-3 py-2.5">{row.counter || "—"}</td>
                          <td className="px-3 py-2.5">{row.store}</td>
                          <td className="px-3 py-2.5">{row.branch || "—"}</td>
                          <td className="px-3 py-2.5">
                            {row.source === "POS" ? "POS" : "Sales invoice"}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {Number(row.taxRate || 0).toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                      {!pageRows.length && (
                        <tr>
                          <td
                            colSpan="22"
                            className="px-4 py-16 text-center text-sm text-slate-500"
                          >
                            No sales lines match the generated filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pager
                  page={page}
                  pages={totalPages}
                  total={visibleRows.length}
                  pageSize={pageSize}
                  onChange={setPage}
                />
              </section>
            </div>
          )}
        </main>
      </div>
      <ReportFilterModal
        open={showFilters}
        title="Sales detailed filters"
        description="Choose filters, then generate the register. Generating applies the filters and closes this window."
        loading={loading}
        onApply={generate}
        onClose={closeFilters}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <FilterField label="From date">
            <input
              type="date"
              value={draftFilters.from}
              onChange={(event) => updateDraft("from", event.target.value)}
              className="report-input"
            />
          </FilterField>
          <FilterField label="To date">
            <input
              type="date"
              value={draftFilters.to}
              onChange={(event) => updateDraft("to", event.target.value)}
              className="report-input"
            />
          </FilterField>
          <FilterField label="Godown / Store">
            <SearchableSelect
              value={draftFilters.storeId}
              onChange={(value) => updateDraft("storeId", value)}
              placeholder="All stores"
              options={stores.map((store) => ({
                value: store.id,
                label: `${store.name} (${store.code})`,
              }))}
            />
          </FilterField>
          <FilterField label="Series">
            <SearchableSelect
              value={draftFilters.source}
              onChange={(value) => updateDraft("source", value)}
              placeholder="All sales"
              options={[
                { value: "POS", label: "POS" },
                { value: "SALES_INVOICE", label: "Sales invoice" },
              ]}
            />
          </FilterField>
          <FilterField label="Branch">
            <SearchableSelect
              value={draftFilters.branch}
              onChange={(value) => updateDraft("branch", value)}
              placeholder="All branches"
              options={choices.branches}
            />
          </FilterField>
          <FilterField label="Company / Brand">
            <SearchableSelect
              value={draftFilters.brand}
              onChange={(value) => updateDraft("brand", value)}
              placeholder="All brands"
              options={choices.brands}
            />
          </FilterField>
          <FilterField label="Item group">
            <SearchableSelect
              value={draftFilters.category}
              onChange={(value) => updateDraft("category", value)}
              placeholder="All item groups"
              options={choices.categories}
            />
          </FilterField>
          <FilterField label="Agent">
            <SearchableSelect
              value={draftFilters.agent}
              onChange={(value) => updateDraft("agent", value)}
              placeholder="All agents"
              options={choices.agents}
            />
          </FilterField>
          <FilterField label="Cashier">
            <SearchableSelect
              value={draftFilters.cashier}
              onChange={(value) => updateDraft("cashier", value)}
              placeholder="All cashiers"
              options={choices.cashiers}
            />
          </FilterField>
          <FilterField label="Party / Customer">
            <SearchableSelect
              value={draftFilters.customer}
              onChange={(value) => updateDraft("customer", value)}
              placeholder="All customers"
              options={choices.customers}
            />
          </FilterField>
          <FilterField label="Voucher / Bill no.">
            <input
              value={draftFilters.voucher}
              onChange={(event) => updateDraft("voucher", event.target.value)}
              placeholder="Bill number"
              className="report-input"
            />
          </FilterField>
          <FilterField label="Search product, barcode or lot">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={draftFilters.search}
                onChange={(event) => updateDraft("search", event.target.value)}
                placeholder="Product, barcode or lot"
                className="report-input pl-9"
              />
            </div>
          </FilterField>
          <Range
            label="Sale amount"
            min={draftFilters.minAmount}
            max={draftFilters.maxAmount}
            onMin={(value) => updateDraft("minAmount", value)}
            onMax={(value) => updateDraft("maxAmount", value)}
          />
          <Range
            label="Rate"
            min={draftFilters.minRate}
            max={draftFilters.maxRate}
            onMin={(value) => updateDraft("minRate", value)}
            onMax={(value) => updateDraft("maxRate", value)}
          />
        </div>
      </ReportFilterModal>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-xl font-bold text-slate-950">{value}</p>
    </div>
  );
}
function Label({ label, children }) {
  return (
    <label className="block text-xs font-semibold text-slate-600">
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}
function FilterField({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      <span className="mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
function Select({ options, value, onChange }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="filter-input"
    >
      <option value="">All</option>
      {options.map((item) => (
        <option key={item} value={item}>
          {item}
        </option>
      ))}
    </select>
  );
}
function Range({ label, min, max, onMin, onMax }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-slate-600">{label} range</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          min="0"
          value={min}
          onChange={(event) => onMin(event.target.value)}
          placeholder="Min"
          className="filter-input"
        />
        <input
          type="number"
          min="0"
          value={max}
          onChange={(event) => onMax(event.target.value)}
          placeholder="Max"
          className="filter-input"
        />
      </div>
    </div>
  );
}
function Pager({ page, pages, total, pageSize, onChange }) {
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
      <p>
        Showing {start.toLocaleString("en-IN")}–{end.toLocaleString("en-IN")} of{" "}
        {total.toLocaleString("en-IN")}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 font-medium hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FiChevronLeft /> Previous
        </button>
        <span className="text-xs font-semibold">
          Page {page} / {pages}
        </span>
        <button
          type="button"
          disabled={page === pages}
          onClick={() => onChange(page + 1)}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 font-medium hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next <FiChevronRight />
        </button>
      </div>
    </div>
  );
}
